import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { all_routes } from '../../router/all_routes';
import type { AppDispatch, RootState } from '../../../core/data/redux/store';
import { getMyProfile } from '../../../core/data/redux/employeesSlice';
import {
  fetchSapAuthMasterAll,
  createOrUpdateSapAuthRequest,
  fetchSapAuthRequestById,
  ISapAuthorizationTCode,
  ISapMasterOption,
} from '../../../core/data/redux/sapAuthorizationSlice';

const SapAuthorizationCreate: React.FC = () => {
  const routes = all_routes;
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const dispatch = useDispatch<AppDispatch>();
  const employeesState = useSelector((state: RootState) => state.employeesState);
  const sapState = useSelector((state: RootState) => state.sapAuthorizationState);

  const profile = employeesState?.data?.data?.details || employeesState?.data?.details;
  const loggedEmpId = String(profile?.empId || profile?.employee_id || '');
  const employeeFullName =
    [profile?.firstname, profile?.lastname].filter(Boolean).join(' ') ||
    profile?.fullName ||
    profile?.EmployeeName ||
    '';
  const profileDesignation =
    profile?.sap_department_designation_wings?.designation_name || profile?.designation || '';
  const profileDepartment =
    profile?.sap_department_designation_wings?.department_name || profile?.department || '';
  const profileWing =
    profile?.sap_department_designation_wings?.wing_name || profile?.wing || '';

  // Form State
  const [formData, setFormData] = useState({
    Department: '',
    ContactNumber: '',
    Wing: '',
    NewOrExistingUser: 'N', // 'N' (New) or 'E' (Existing)
    ExistingUserID: '',
    PurchaseGroup: '',
    Plant: '',
    BusinessArea: '',
    PurchasingOrganization: '',
    SAPServer: 'PRD',
    SAPModule: 'MM',
    AuthorizationRevoke: 'A', // 'A' (N/A), 'N' (No), 'Y' (Yes)
    RevokedEmployeeNumber: '',
    Remarks: '',
  });

  const [tcodes, setTcodes] = useState<ISapAuthorizationTCode[]>([]);
  const [newTcode, setNewTcode] = useState({ code: '', remarks: '' });
  const [showAddModal, setShowAddModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    dispatch(getMyProfile({}));
    dispatch(fetchSapAuthMasterAll({}));
  }, [dispatch]);

  // Load existing data if edit mode
  useEffect(() => {
    if (isEditMode && id) {
      dispatch(fetchSapAuthRequestById({ ReferenceNumber: id }));
    }
  }, [dispatch, isEditMode, id]);

  // Fill profile details when available
  useEffect(() => {
    if (profile && !isEditMode) {
      setFormData((prev) => ({
        ...prev,
        Department: prev.Department || profileDepartment || '',
        ContactNumber: prev.ContactNumber || profile?.phone_number || profile?.mobile || '',
        Wing: prev.Wing || (profileWing ? 'PROJ' : ''),
      }));
    }
  }, [profile, isEditMode, profileDepartment, profileWing]);

  // Fill edit data when loaded
  useEffect(() => {
    if (isEditMode && sapState.detailData) {
      const d = sapState.detailData;
      setFormData({
        Department: d.Department || '',
        ContactNumber: d.ContactNumber || '',
        Wing: d.Wing || '',
        NewOrExistingUser: d.NewOrExistingUser || 'N',
        ExistingUserID: d.ExistingUserID || '',
        PurchaseGroup: d.PurchaseGroup || '',
        Plant: d.Plant || '',
        BusinessArea: d.BusinessArea || '',
        PurchasingOrganization: d.PurchasingOrganization || '',
        SAPServer: d.SAPServer || 'PRD',
        SAPModule: d.SAPModule || 'MM',
        AuthorizationRevoke: d.AuthorizationRevoke || 'A',
        RevokedEmployeeNumber: d.ExistingUserID || '',
        Remarks: d.Remarks || d.remarks || '',
      });

      const rawTcodes: any = d.ToTcodes;
      const loadedTcodes: ISapAuthorizationTCode[] = rawTcodes && typeof rawTcodes === 'object' && Array.isArray(rawTcodes.results)
        ? rawTcodes.results
        : Array.isArray(rawTcodes)
          ? rawTcodes
          : Array.isArray((d as any)?.results)
            ? (d as any).results
            : [];

      if (loadedTcodes.length > 0) {
        setTcodes(
          loadedTcodes.map((t, idx) => ({
            SrNo: String(t.SrNo || idx + 1).padStart(2, '0'),
            TransactionCode: String(t.TransactionCode || '').trim().toUpperCase(),
            Remarks: t.Remarks || '',
          }))
        );
      }
    }
  }, [isEditMode, sapState.detailData]);

  const masterData = sapState.masterData || {};
  const plantsList: ISapMasterOption[] = masterData.Plants || [];
  const wingsList: ISapMasterOption[] = masterData.Wings || [];
  const userCatList: ISapMasterOption[] = masterData.User_Categories || [];
  const serversList: ISapMasterOption[] = masterData.SAP_servers || [];
  const modulesList: ISapMasterOption[] = masterData.SAP_Modules || [];
  const businessAreasList: ISapMasterOption[] = masterData.Business_Areas || [];
  const purchaseOrgsList: ISapMasterOption[] = masterData.Purchase_Organizations || [];
  const authRevokedList: ISapMasterOption[] = masterData.Authorization_Revoked || [];

  // Set initial default options when master loads
  useEffect(() => {
    if (!formData.Plant && plantsList.length > 0) {
      setFormData((prev) => ({ ...prev, Plant: plantsList[0].Value }));
    }
    if (!formData.BusinessArea && businessAreasList.length > 0) {
      setFormData((prev) => ({ ...prev, BusinessArea: businessAreasList[0].Value }));
    }
    if (!formData.PurchasingOrganization && purchaseOrgsList.length > 0) {
      setFormData((prev) => ({ ...prev, PurchasingOrganization: purchaseOrgsList[0].Value }));
    }
    if (!formData.Wing && wingsList.length > 0) {
      setFormData((prev) => ({ ...prev, Wing: wingsList[0].Value }));
    }
  }, [plantsList, businessAreasList, purchaseOrgsList, wingsList]);

  // Handle T-Code addition
  const handleAddTCode = () => {
    const cleanCode = newTcode.code.trim().toUpperCase();
    if (!cleanCode) {
      toast.warning('Please enter a valid SAP Transaction Code');
      return;
    }
    if (tcodes.some((t) => t.TransactionCode.toUpperCase() === cleanCode)) {
      toast.warning(`Transaction code ${cleanCode} is already added in the list`);
      return;
    }

    const nextSrNo = String(tcodes.length + 1).padStart(2, '0');
    setTcodes([...tcodes, { SrNo: nextSrNo, TransactionCode: cleanCode, Remarks: newTcode.remarks.trim() }]);
    setNewTcode({ code: '', remarks: '' });
    setShowAddModal(false);
  };

  const handleRemoveTCode = (index: number) => {
    const updated = tcodes.filter((_, idx) => idx !== index);
    const reindexed = updated.map((t, idx) => ({
      ...t,
      SrNo: String(idx + 1).padStart(2, '0'),
    }));
    setTcodes(reindexed);
  };

  const handleAddPresetTCode = (preset: { code: string; label: string }) => {
    const cleanCode = preset.code.toUpperCase();
    if (tcodes.some((t) => t.TransactionCode.toUpperCase() === cleanCode)) {
      toast.info(`${cleanCode} is already in your requested T-Codes list`);
      return;
    }
    const nextSrNo = String(tcodes.length + 1).padStart(2, '0');
    setTcodes([...tcodes, { SrNo: nextSrNo, TransactionCode: cleanCode, Remarks: preset.label }]);
    toast.success(`Added ${cleanCode} (${preset.label})`);
  };

  // Form Validation
  const validateForm = (isDraft: boolean) => {
    const errs: Record<string, string> = {};

    if (!formData.ContactNumber.trim()) {
      errs.ContactNumber = 'Contact Number is required';
    } else if (!/^[0-9]{10}$/.test(formData.ContactNumber.replace(/[^0-9]/g, ''))) {
      errs.ContactNumber = 'Please enter a valid 10-digit mobile number';
    }

    if (!formData.Department.trim()) {
      errs.Department = 'Department is required';
    }

    if (!isDraft) {
      if (!formData.Plant) errs.Plant = 'Please select a Plant';
      if (!formData.BusinessArea) errs.BusinessArea = 'Please select a Business Area';
      if (!formData.SAPModule) errs.SAPModule = 'Please select an SAP Module';
      if (!formData.SAPServer) errs.SAPServer = 'Please select an SAP Server';
      if (formData.AuthorizationRevoke === 'Y' && !formData.RevokedEmployeeNumber.trim()) {
        errs.RevokedEmployeeNumber = 'Please provide the employee number whose authorizations are to be revoked';
      }
      if (tcodes.length === 0) {
        errs.tcodes = 'Please add at least one Transaction Code (T-Code) to submit';
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Handle Form Submission
  const handleSubmit = async (isDraft: boolean) => {
    if (!validateForm(isDraft)) {
      toast.error('Please fill in all required fields accurately');
      return;
    }

    setIsSubmitting(true);

    const empNo = loggedEmpId || profile?.empId || '';

    const payload: any = {
      draft: isDraft,
      Action: isDraft ? 'D' : 'V',
      Department: formData.Department.trim(),
      ContactNumber: formData.ContactNumber.trim(),
      Wing: formData.Wing || 'PROJ',
      NewOrExistingUser: formData.NewOrExistingUser,
      ExistingUserID: formData.AuthorizationRevoke === 'Y' ? formData.RevokedEmployeeNumber.trim() : '',
      PurchaseGroup: formData.PurchaseGroup.trim(),
      Plant: formData.Plant,
      BusinessArea: formData.BusinessArea,
      PurchasingOrganization: formData.PurchasingOrganization,
      SAPServer: formData.SAPServer,
      SAPModule: formData.SAPModule,
      AuthorizationRevoke: formData.AuthorizationRevoke,
      Remarks: formData.Remarks.trim(),
      ToTcodes: {
        results: tcodes.map((t, idx) => ({
          SrNo: String(idx + 1).padStart(2, '0'),
          TransactionCode: t.TransactionCode.trim().toUpperCase(),
          Remarks: t.Remarks.trim() || 'Required for official operational duties',
        })),
      },
    };

    if (isEditMode && sapState.detailData?.id) {
      payload.id = sapState.detailData.id;
    }

    try {
      const res = await dispatch(createOrUpdateSapAuthRequest(payload));
      if (res.meta.requestStatus === 'fulfilled') {
        toast.success(
          isDraft
            ? 'SAP Authorization request saved as draft successfully!'
            : isEditMode
              ? 'SAP Authorization request updated successfully!'
              : 'SAP Authorization request submitted successfully!'
        );
        navigate(routes.sapAuthorization.list);
      } else {
        toast.error((res.payload as string) || 'Failed to submit SAP Authorization request');
      }
    } catch {
      toast.error('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Header */}
        <div className="d-md-flex d-block align-items-center justify-content-between mb-4">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1 text-dark fw-bold">
              <i className="ti ti-lock-access me-2 text-danger"></i>
              SAP ERP Authorization/ID Creation Request Form
            </h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.sapAuthorization.list}>SAP Authorization Forms</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {isEditMode ? 'Edit Request' : 'New Request'}
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Link to={routes.sapAuthorization.list} className="btn btn-outline-secondary d-flex align-items-center">
              <i className="ti ti-arrow-left me-1"></i>Back to List
            </Link>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit(false);
          }}
        >
          {/* USER DETAILS */}
          <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
            <div className="card-header bg-white border-bottom py-3 px-4">
              <h5 className="mb-0 fw-bold text-dark text-uppercase">
                <i className="ti ti-user-check me-2 text-danger"></i>USER DETAILS
              </h5>
            </div>
            <div className="card-body p-4">
              <div className="row g-4">
                {/* User Category Radio */}
                <div className="col-12 mb-2">
                  <label className="form-label fs-13 fw-semibold text-danger mb-2">
                    User Category <span className="text-danger">*</span>
                  </label>
                  <div className="d-flex align-items-center gap-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="userCategory"
                        id="userCategoryExisting"
                        checked={formData.NewOrExistingUser === 'E'}
                        onChange={() => setFormData({ ...formData, NewOrExistingUser: 'E' })}
                      />
                      <label className="form-check-label fw-semibold text-dark cursor-pointer" htmlFor="userCategoryExisting">
                        Existing SAP User
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="userCategory"
                        id="userCategoryNew"
                        checked={formData.NewOrExistingUser === 'N'}
                        onChange={() => setFormData({ ...formData, NewOrExistingUser: 'N' })}
                      />
                      <label className="form-check-label fw-semibold text-dark cursor-pointer" htmlFor="userCategoryNew">
                        New SAP User
                      </label>
                    </div>
                  </div>
                </div>

                <div className="col-md-4 col-sm-6">
                  <label className="form-label fs-13 fw-semibold text-muted mb-1">Employee No.</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={loggedEmpId || profile?.empId || '-'}
                    readOnly
                    disabled
                  />
                </div>

                <div className="col-md-4 col-sm-6">
                  <label className="form-label fs-13 fw-semibold text-muted mb-1">Name</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={employeeFullName || '-'}
                    readOnly
                    disabled
                  />
                </div>

                <div className="col-md-4 col-sm-6">
                  <label className="form-label fs-13 fw-semibold text-muted mb-1">Designation</label>
                  <input
                    type="text"
                    className="form-control bg-light"
                    value={profileDesignation || '-'}
                    readOnly
                    disabled
                  />
                </div>

                <div className="col-md-4 col-sm-6">
                  <label className="form-label fs-13 fw-semibold text-dark mb-1">
                    Department <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.Department ? 'is-invalid' : ''}`}
                    placeholder="Enter Department"
                    value={formData.Department}
                    onChange={(e) => setFormData({ ...formData, Department: e.target.value })}
                  />
                  {errors.Department && <div className="invalid-feedback">{errors.Department}</div>}
                </div>

                <div className="col-md-4 col-sm-6">
                  <label className="form-label fs-13 fw-semibold text-dark mb-1">
                    Phone Number <span className="text-danger">*</span>
                  </label>
                  <input
                    type="tel"
                    className={`form-control ${errors.ContactNumber ? 'is-invalid' : ''}`}
                    placeholder="9958XXXXXX"
                    maxLength={10}
                    value={formData.ContactNumber}
                    onChange={(e) => setFormData({ ...formData, ContactNumber: e.target.value })}
                  />
                  {errors.ContactNumber && <div className="invalid-feedback">{errors.ContactNumber}</div>}
                </div>

                <div className="col-md-4 col-sm-6">
                  <label className="form-label fs-13 fw-semibold text-dark mb-1">
                    Wing <span className="text-danger">*</span>
                  </label>
                  <select
                    className="form-select"
                    value={formData.Wing}
                    onChange={(e) => setFormData({ ...formData, Wing: e.target.value })}
                  >
                    <option value="">Select...</option>
                    {wingsList.map((w) => (
                      <option key={w.Value} value={w.Value}>
                        {w.Ddtext} ({w.Value})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* AUTHORIZATION DETAILS */}
          <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white">
            <div className="card-header bg-white border-bottom py-3 px-4">
              <h5 className="mb-0 fw-bold text-dark text-uppercase">
                <i className="ti ti-server me-2 text-danger"></i>AUTHORIZATION DETAILS
              </h5>
            </div>
            <div className="card-body p-4">
              <div className="row g-4">
                <div className="col-md-6 col-sm-12">
                  <label className="form-label fs-13 fw-semibold text-dark mb-1">Purchasing Group</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter purchasing group"
                    value={formData.PurchaseGroup}
                    onChange={(e) => setFormData({ ...formData, PurchaseGroup: e.target.value })}
                  />
                </div>

                <div className="col-md-6 col-sm-12">
                  <label className="form-label fs-13 fw-semibold text-dark mb-1">Plant</label>
                  <select
                    className={`form-select ${errors.Plant ? 'is-invalid' : ''}`}
                    value={formData.Plant}
                    onChange={(e) => setFormData({ ...formData, Plant: e.target.value })}
                  >
                    <option value="">Select...</option>
                    {plantsList.map((p) => (
                      <option key={p.Value} value={p.Value}>
                        {p.Value} - {p.Ddtext}
                      </option>
                    ))}
                  </select>
                  {errors.Plant && <div className="invalid-feedback">{errors.Plant}</div>}
                </div>

                <div className="col-md-6 col-sm-12">
                  <label className="form-label fs-13 fw-semibold text-dark mb-1">Business Area</label>
                  <select
                    className={`form-select ${errors.BusinessArea ? 'is-invalid' : ''}`}
                    value={formData.BusinessArea}
                    onChange={(e) => setFormData({ ...formData, BusinessArea: e.target.value })}
                  >
                    <option value="">Select...</option>
                    {businessAreasList.map((b) => (
                      <option key={b.Value} value={b.Value}>
                        {b.Value} - {b.Ddtext}
                      </option>
                    ))}
                  </select>
                  {errors.BusinessArea && <div className="invalid-feedback">{errors.BusinessArea}</div>}
                </div>

                <div className="col-md-6 col-sm-12">
                  <label className="form-label fs-13 fw-semibold text-dark mb-1">Purchasing Organization</label>
                  <select
                    className="form-select"
                    value={formData.PurchasingOrganization}
                    onChange={(e) => setFormData({ ...formData, PurchasingOrganization: e.target.value })}
                  >
                    <option value="">Select...</option>
                    {purchaseOrgsList.map((po) => (
                      <option key={po.Value} value={po.Value}>
                        {po.Value} - {po.Ddtext}
                      </option>
                    ))}
                  </select>
                </div>

                {/* SAP Server Selection */}
                <div className="col-md-6 col-sm-12">
                  <label className="form-label fs-13 fw-semibold text-danger mb-1">
                    SAP Server <span className="text-danger">*</span>
                  </label>
                  <select
                    className={`form-select ${errors.SAPServer ? 'is-invalid' : ''}`}
                    value={formData.SAPServer}
                    onChange={(e) => setFormData({ ...formData, SAPServer: e.target.value })}
                  >
                    <option value="">Select...</option>
                    {serversList.map((srv) => (
                      <option key={srv.Value} value={srv.Value}>
                        {srv.Value} - {srv.Ddtext}
                      </option>
                    ))}
                  </select>
                  {errors.SAPServer && <div className="invalid-feedback">{errors.SAPServer}</div>}
                </div>

                {/* SAP Module Selection */}
                <div className="col-md-6 col-sm-12">
                  <label className="form-label fs-13 fw-semibold text-dark mb-1">SAP module</label>
                  <select
                    className={`form-select ${errors.SAPModule ? 'is-invalid' : ''}`}
                    value={formData.SAPModule}
                    onChange={(e) => setFormData({ ...formData, SAPModule: e.target.value })}
                  >
                    <option value="">Select...</option>
                    {modulesList.map((mod) => (
                      <option key={mod.Value} value={mod.Value}>
                        {mod.Value} - {mod.Ddtext}
                      </option>
                    ))}
                  </select>
                  {errors.SAPModule && <div className="invalid-feedback">{errors.SAPModule}</div>}
                </div>

                {/* Transaction Codes Header with Counter and Add Button */}
                <div className="col-12">
                  <div className="d-flex align-items-center justify-content-between border-bottom pb-2 mb-3">
                    <label className="form-label fs-13 fw-semibold text-danger mb-0">
                      Transaction Codes with Description <span className="text-danger">*</span>
                      <span className="badge bg-light text-muted ms-2 fs-11 border">{tcodes.length}/20</span>
                    </label>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm rounded-circle d-flex align-items-center justify-content-center p-0"
                      style={{ width: '32px', height: '32px' }}
                      onClick={() => setShowAddModal(true)}
                      title="Add Transaction Code"
                    >
                      <i className="ti ti-plus fs-16"></i>
                    </button>
                  </div>

                  {tcodes.length === 0 ? (
                    <div className="text-center py-4 text-muted fs-13">
                      No transaction codes added yet
                      {errors.tcodes && (
                        <div className="text-danger mt-1 fw-semibold fs-12">{errors.tcodes}</div>
                      )}
                    </div>
                  ) : (
                    /* T-Codes Table - Shown when data is filled */
                    <div className="table-responsive">
                      <table className="table table-bordered align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '80px' }} className="text-center">Sr No</th>
                            <th style={{ width: '220px' }}>Transaction Code</th>
                            <th>Remarks / Description</th>
                            <th style={{ width: '90px' }} className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {tcodes.map((t, idx) => (
                            <tr key={idx}>
                              <td className="text-center fw-bold text-muted">{t.SrNo || idx + 1}</td>
                              <td>
                                <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-2 fs-13 font-monospace">
                                  {t.TransactionCode}
                                </span>
                              </td>
                              <td>
                                <input
                                  type="text"
                                  className="form-control form-control-sm"
                                  placeholder="Enter description or remarks..."
                                  value={t.Remarks}
                                  onChange={(e) => {
                                    const updated = [...tcodes];
                                    updated[idx].Remarks = e.target.value;
                                    setTcodes(updated);
                                  }}
                                />
                              </td>
                              <td className="text-center">
                                <button
                                  type="button"
                                  className="btn btn-xs btn-outline-danger px-2"
                                  title="Delete T-Code"
                                  onClick={() => handleRemoveTCode(idx)}
                                >
                                  <i className="ti ti-trash"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>

                {/* Revocation Radio Options */}
                <div className="col-12 mt-4">
                  <label className="form-label fs-13 fw-semibold text-danger mb-2">
                    In case of requesting transactions of already existing user, whether authorizations to be revoked from existing user <span className="text-danger">*</span>
                  </label>
                  <div className="d-flex align-items-center flex-wrap gap-4">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="revocationChoice"
                        id="revocationNA"
                        checked={formData.AuthorizationRevoke === 'A'}
                        onChange={() => setFormData({ ...formData, AuthorizationRevoke: 'A' })}
                      />
                      <label className="form-check-label text-dark cursor-pointer" htmlFor="revocationNA">
                        Not Applicable
                      </label>
                    </div>

                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="revocationChoice"
                        id="revocationNo"
                        checked={formData.AuthorizationRevoke === 'N'}
                        onChange={() => setFormData({ ...formData, AuthorizationRevoke: 'N' })}
                      />
                      <label className="form-check-label text-dark cursor-pointer" htmlFor="revocationNo">
                        No
                      </label>
                    </div>

                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="revocationChoice"
                        id="revocationYes"
                        checked={formData.AuthorizationRevoke === 'Y'}
                        onChange={() => setFormData({ ...formData, AuthorizationRevoke: 'Y' })}
                      />
                      <label className="form-check-label text-dark cursor-pointer" htmlFor="revocationYes">
                        Yes
                      </label>
                    </div>
                  </div>
                </div>

                {/* Revoked Employee Number Field */}
                {formData.AuthorizationRevoke === 'Y' && (
                  <div className="col-md-6 col-sm-12 mt-3">
                    <label className="form-label fs-13 fw-semibold text-danger mb-1">
                      Employee number of user whose authorizations are to be revoked <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${errors.RevokedEmployeeNumber ? 'is-invalid' : ''}`}
                      placeholder="Enter Employee Number"
                      value={formData.RevokedEmployeeNumber}
                      onChange={(e) => setFormData({ ...formData, RevokedEmployeeNumber: e.target.value })}
                    />
                    {errors.RevokedEmployeeNumber && <div className="invalid-feedback">{errors.RevokedEmployeeNumber}</div>}
                  </div>
                )}

                {/* Any Remarks */}
                <div className="col-12 mt-3">
                  <label className="form-label fs-13 fw-semibold text-dark mb-1">Any Remarks</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    placeholder="Write Something"
                    value={formData.Remarks}
                    onChange={(e) => setFormData({ ...formData, Remarks: e.target.value })}
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="d-flex align-items-center justify-content-end gap-3 mb-5">
            <button
              type="button"
              className="btn btn-secondary px-5 py-2 fw-semibold"
              disabled={isSubmitting}
              onClick={() => handleSubmit(true)}
            >
              Save as Draft
            </button>
            <button
              type="submit"
              className="btn btn-danger px-5 py-2 fw-semibold"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  Submitting...
                </>
              ) : (
                'Submit'
              )}
            </button>
          </div>
        </form>


        {/* Add T-Code Modal */}
        {showAddModal && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg rounded-3">
                <div className="modal-header border-bottom py-3 px-4">
                  <h5 className="modal-title fw-bold text-dark">
                    <i className="ti ti-plus text-danger me-2"></i>Add Custom SAP Transaction Code
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowAddModal(false)}
                  ></button>
                </div>
                <div className="modal-body p-4">
                  <div className="mb-3">
                    <label className="form-label fs-13 fw-semibold text-dark mb-1">
                      SAP Transaction Code (T-Code) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-control text-uppercase font-monospace fw-bold"
                      placeholder="e.g. ME21N, FB60, PA30"
                      value={newTcode.code}
                      onChange={(e) => setNewTcode({ ...newTcode, code: e.target.value.toUpperCase() })}
                      autoFocus
                    />
                    <small className="text-muted fs-11">Enter the exact SAP transaction code name.</small>
                  </div>

                  <div className="mb-2">
                    <label className="form-label fs-13 fw-semibold text-dark mb-1">Purpose / Remarks</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. Creation of Work Purchase Orders"
                      value={newTcode.remarks}
                      onChange={(e) => setNewTcode({ ...newTcode, remarks: e.target.value })}
                    />
                  </div>
                </div>
                <div className="modal-footer border-top py-3 px-4">
                  <button
                    type="button"
                    className="btn btn-outline-secondary me-2"
                    onClick={() => setShowAddModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="button" className="btn btn-danger px-4" onClick={handleAddTCode}>
                    <i className="ti ti-plus me-1"></i>Add to List
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SapAuthorizationCreate;
